import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit, Trash } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AttributeValue } from "@/types/product-type";
import { getAttributeColors } from "@/constants/product-attributes";

interface ProductAttributeValuesTableProps {
  values: AttributeValue[];
  attributeName: string;
  onEdit: (value: AttributeValue) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

export const ProductAttributeValuesTable = ({
  values,
  attributeName,
  onEdit,
  onDelete,
  isLoading = false,
}: ProductAttributeValuesTableProps) => {
  const colors = getAttributeColors(attributeName);
  const hasColorPicker = colors.hasColorPicker;

  if (isLoading) {
    return (
      <div className="flex justify-center my-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (values.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 mb-4">No values found for this attribute</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Value</TableHead>
          <TableHead>Display Value</TableHead>
          {hasColorPicker && <TableHead>Preview</TableHead>}
          <TableHead className="text-right">Sort Order</TableHead>
          <TableHead className="text-center">Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {values.map((value) => (
          <TableRow key={value.id}>
            <TableCell className="font-medium">{value.value}</TableCell>
            <TableCell>{value.displayValue || value.value}</TableCell>
            {hasColorPicker && (
              <TableCell>
                {value.colorCode ? (
                  <div
                    className="w-6 h-6 rounded-full border border-gray-300"
                    style={{ backgroundColor: value.colorCode }}
                  />
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </TableCell>
            )}
            <TableCell className="text-right">{value.sortOrder}</TableCell>
            <TableCell className="text-center">
              <span
                className={value.isActive ? "text-green-600" : "text-red-600"}
              >
                {value.isActive ? "Active" : "Inactive"}
              </span>
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(value)}
                  className="hover:bg-gray-100"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="hover:bg-gray-100"
                    >
                      <Trash className="h-4 w-4 text-red-500" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Delete Attribute Value
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this value? This may
                        affect products using this value.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-600 text-white hover:bg-red-700"
                        onClick={() => onDelete(value.id)}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
